import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Pagination,
  Menu,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  GridView as GridIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  ViewList as ListIcon,
} from '@mui/icons-material';
import {
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import FileUploader from '../components/FileUploader';
import { fetchMedia, uploadMedia, removeMedia, setFilter, fetchS3Media } from '../redux/slices/mediaSlice';
import { fetchTVsFromAllBranches } from '../redux/slices/tvSlice';
import { extractS3KeyFromUrl, readSlideConfig, updateSlideConfig } from '../services/s3Service';
import { generateUniqueId } from '../utils/helpers';
import { updateStatus } from '../services/firebaseService';
import { colors, naturalScale, borderRadius } from '../theme/tokens';
import NoMediaImage from '../assets/images/no-media.svg';
import {
  RiUploadCloudFill,
  RiDeleteBinLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiImageLine,
  RiVideoLine,
  RiGridLine,
  RiRefreshLine,
  RiStore2Fill,
  RiListCheck,
  RiMore2Fill,
  RiPlayLine,
  RiImageCircleLine,
} from '@remixicon/react';
import PaginationItem from '@mui/material/PaginationItem';

const MediaLibrary = () => {
  const dispatch = useDispatch();
  const { allMedia, images, videos, s3Media, loading, loadingS3, error, filter } = useSelector((state) => state.media);
  const { takeawayList } = useSelector((state) => state.auth);
  const { tvs } = useSelector((state) => state.tv);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('row');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(8);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [uploadBranch, setUploadBranch] = useState(null);
  const [tvSelectionModalOpen, setTvSelectionModalOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const userId = 'demo-user'; // Replace with actual user ID

  // Extract branches from takeawayList (memoized to prevent infinite loops)
  const branches = useMemo(() => {
    if (!takeawayList || takeawayList.length === 0) return [];
    return takeawayList.map((takeaway) => ({
      id: takeaway.license_key || takeaway.id,
      name: takeaway.name || takeaway.branch_name || takeaway.license_key || 'Unknown Branch',
      value: takeaway.license_key || takeaway.id,
      store_id: takeaway.store_id || takeaway.license_key || takeaway.id,
    }));
  }, [takeawayList]);

  const [selectedBranch, setSelectedBranch] = useState(null);
  const hasInitialized = useRef(false);

  // Set default branch when branches are available (only once)
  useEffect(() => {
    if (branches.length > 0 && !selectedBranch && !hasInitialized.current) {
      const firstBranch = branches[0];
      setSelectedBranch(firstBranch.value);
      hasInitialized.current = true;
    }
  }, [branches.length, selectedBranch]); // Only depend on branches.length and selectedBranch

  // Filter TVs by selected branch
  const filteredTVs = useMemo(() => {
    if (!selectedBranch || !tvs || tvs.length === 0) return [];
    const branch = branches.find((b) => b.value === selectedBranch);
    const storeId = branch?.store_id;
    if (!storeId) return [];

    // Filter TVs that belong to this storeId
    // TVs from fetchTVsByStoreId have storeId (camelCase) field
    return tvs.filter((tv) => {
      // Check storeId (camelCase - from Firebase)
      if (tv.storeId === storeId) return true;
      // Check store_id (snake_case - fallback)
      if (tv.store_id === storeId) return true;
      // Check branch field (license_key or branch name)
      if (tv.branch === selectedBranch || tv.branch === branch?.value || tv.branch === branch?.name) return true;
      return false;
    });
  }, [selectedBranch, tvs, branches]);

  // Fetch TVs from all branches on mount
  useEffect(() => {
    if (takeawayList && takeawayList.length > 0) {
      const storeIds = takeawayList.map((takeaway) => takeaway.store_id).filter(Boolean);
      if (storeIds.length > 0) {
        console.log('Fetching TVs for storeIds:', storeIds);
        dispatch(fetchTVsFromAllBranches(storeIds));
      }
    }
  }, [dispatch, takeawayList]);

  // Debug: Log TVs and filtered TVs
  useEffect(() => {
    console.log('TVs in state:', tvs);
    console.log('Selected Branch:', selectedBranch);
    console.log('Branches:', branches);
    if (selectedBranch && branches.length > 0) {
      const branch = branches.find((b) => b.value === selectedBranch);
      console.log('Current Branch:', branch);
      console.log('Store ID for filtering:', branch?.store_id);
    }
    console.log('Filtered TVs:', filteredTVs);
  }, [tvs, selectedBranch, filteredTVs, branches]);

  // Fetch media from S3 when branch changes
  useEffect(() => {
    if (selectedBranch && branches.length > 0) {
      const branch = branches.find((b) => b.value === selectedBranch);
      const storeId = branch?.store_id;
      if (storeId) {
        dispatch(fetchS3Media(storeId));
      } else {
        dispatch(fetchS3Media(null)); // Clear S3 media if no storeId
      }
    } else if (!selectedBranch && branches.length === 0) {
      dispatch(fetchS3Media(null)); // Clear S3 media if no branch selected
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, branches.length]); // Only depend on selectedBranch and branches.length (branches is memoized)

  const handleUpload = async (file, type) => {
    if (!uploadBranch) {
      alert('Please select a branch before uploading');
      return;
    }

    const branch = branches.find((b) => b.value === uploadBranch);
    const storeId = branch?.store_id;

    if (!storeId) {
      alert('Invalid branch selected. Please try again.');
      return;
    }

    try {
      await dispatch(uploadMedia({ userId, file, type, storeId })).unwrap();
      // Refresh S3 media after successful upload
      dispatch(fetchS3Media(storeId));
    } catch (error) {
      console.error('Upload failed:', error);
      alert(`Upload failed: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDelete = async (mediaId) => {
    if (window.confirm('Are you sure you want to delete this media?')) {
      // Find media in either s3Media or allMedia
      const media = s3Media.find((m) => m.id === mediaId) || allMedia.find((m) => m.id === mediaId);

      if (!media) {
        alert('Media not found');
        return;
      }

      // Get s3Key from media object (s3Key property or extract from URL)
      let s3Key = media.s3Key;
      if (!s3Key && media.url) {
        s3Key = extractS3KeyFromUrl(media.url);
      }

      if (!s3Key) {
        alert('Cannot delete: S3 key not found');
        return;
      }

      try {
        await dispatch(removeMedia({ userId, mediaId, s3Key })).unwrap();

        // Refresh S3 media for the current branch after deletion
        if (selectedBranch && branches.length > 0) {
          const branch = branches.find((b) => b.value === selectedBranch);
          const storeId = branch?.store_id;
          if (storeId) {
            dispatch(fetchS3Media(storeId));
          }
        }
      } catch (error) {
        console.error('Delete failed:', error);
        alert(`Delete failed: ${error.message || 'Unknown error'}`);
      }
    }
  };
  const handleActionClick = (event, media) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedMedia(media);
  };
  const handleActionClose = () => {
    setActionMenuAnchor(null);
    setSelectedMedia(null);
  };

  const handleRowClick = (media) => {
    setPreviewMedia(media);
    setPreviewModalOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewModalOpen(false);
    setPreviewMedia(null);
  };
  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      dispatch(setFilter(newFilter));
    }
  };

  const handleAssignToTV = async (tv) => {
    if (!selectedMedia || !selectedBranch) {
      alert('Please select a branch and media first');
      return;
    }

    const branch = branches.find((b) => b.value === selectedBranch);
    const storeId = branch?.store_id;

    if (!storeId) {
      alert('Invalid branch selected');
      return;
    }

    setAssigning(true);
    try {
      // Create media object based on type
      const mediaObject = selectedMedia.type === 'image'
        ? {
          id: generateUniqueId(),
          type: 'Image',
          uri: selectedMedia.url,
          tvId: tv.id,
          tvName: tv.name || `TV-${tv.id}`,
          updatedAt: new Date().toISOString(),
        }
        : {
          id: generateUniqueId(),
          type: 'Video',
          videoUrl: selectedMedia.url,
          previewUrl: selectedMedia.url,
          tvId: tv.id,
          tvName: tv.name || `TV-${tv.id}`,
          updatedAt: new Date().toISOString(),
        };

      // Update slideconfig.json
      await updateSlideConfig(storeId, tv.id, mediaObject);

      // Update TV status (slides_updated_at)
      await updateStatus(tv.serialNumber);

      alert(`Media successfully assigned to ${tv.name || `TV-${tv.id}`}`);
      setTvSelectionModalOpen(false);
      setSelectedMedia(null); // Clear selected media after successful assignment
    } catch (error) {
      console.error('Error assigning media to TV:', error);
      alert(`Failed to assign media: ${error.message || 'Unknown error'}`);
    } finally {
      setAssigning(false);
    }
  };

  // Use only S3 media for the selected branch (since uploads go directly to S3)
  // This prevents duplicates and ensures we only show media for the selected branch
  const combinedMedia = s3Media;
  const combinedImages = combinedMedia.filter((m) => m.type === 'image');
  const combinedVideos = combinedMedia.filter((m) => m.type === 'video');

  const filteredMedia =
    filter === 'all' ? combinedMedia : filter === 'image' ? combinedImages : combinedVideos;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedMedia = filteredMedia.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const totalPages = Math.ceil(filteredMedia.length / rowsPerPage) || 1;

  if ((loading || loadingS3) && filteredMedia.length === 0 && s3Media.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}


      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            mb: 3,
            pr: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<RiUploadCloudFill />}
            onClick={() => setUploadModalOpen(true)}
            size="large"
          >
            Upload Media
          </Button>
        </Box>


        <TableContainer
          component={Paper}
          sx={{
            borderRadius: borderRadius.xxxs,
            boxShadow: 'none',
            border: `1px solid ${colors.neutral.gray200}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              bgcolor: naturalScale[150],
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, color: naturalScale[5] }}>
                Uploaded media
              </Typography>
              <Typography variant="body2" sx={{ color: naturalScale[65] }}>
                {filteredMedia.length} items
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* Media Type Filters */}
              <ToggleButtonGroup
                value={filter}
                exclusive
                onChange={(e, newFilter) => {
                  if (newFilter !== null) {
                    dispatch(setFilter(newFilter));
                  }
                }}
                sx={{
                  bgcolor: colors.neutral.white,
                  borderRadius: borderRadius.xxxs,
                  border: `1px solid ${colors.neutral.gray200}`,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    px: 2,
                    py: 0.75,
                    textTransform: 'none',
                    color: naturalScale[65],
                    '&.Mui-selected': {
                      bgcolor: naturalScale[150],
                      color: naturalScale[5],
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: naturalScale[150],
                      },
                    },
                    '&:hover': {
                      bgcolor: naturalScale[150],
                    },
                  },
                }}
              >
                <ToggleButton value="all" aria-label="all media">
                  All
                </ToggleButton>
                <ToggleButton value="image" aria-label="images">
                  <RiImageLine size={18} style={{ marginRight: 6 }} />
                  Image
                </ToggleButton>
                <ToggleButton value="video" aria-label="videos">
                  <RiVideoLine size={18} style={{ marginRight: 6 }} />
                  Video
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Branch Selector */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  displayEmpty
                  startAdornment={
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <RiStore2Fill size={20} color={naturalScale[50]} />
                    </Box>
                  }
                  sx={{
                    bgcolor: colors.neutral.white,
                    borderRadius: borderRadius.xxxs,
                    border: `1px solid ${colors.neutral.gray200}`,
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  {/* <MenuItem value="all">All Branches</MenuItem> */}
                  {branches.map((branch) => (
                    <MenuItem key={branch.id || branch.value} value={branch.value}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Grid View Toggle */}
              <IconButton
                onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
                sx={{
                  borderRadius: borderRadius.xxxs,
                  border: `1px solid ${colors.neutral.gray200}`,
                  bgcolor: colors.neutral.white,
                  p: 1,
                  '&:hover': {
                    bgcolor: naturalScale[150],
                  },
                }}
              >
                {viewMode === 'table' ? <RiGridLine size={20} color={naturalScale[50]} /> : <RiListCheck size={20} color={naturalScale[50]} />}
              </IconButton>

              {/* Refresh Button */}
              <IconButton
                onClick={() => {
                  dispatch(fetchMedia(userId));
                  if (selectedBranch && branches.length > 0) {
                    const branch = branches.find((b) => b.value === selectedBranch);
                    const storeId = branch?.store_id;
                    if (storeId) {
                      dispatch(fetchS3Media(storeId));
                    }
                  }
                }}
                sx={{
                  borderRadius: borderRadius.xxxs,
                  border: `1px solid ${colors.neutral.gray200}`,
                  bgcolor: colors.neutral.white,
                  p: 1,
                  '&:hover': {
                    bgcolor: naturalScale[150],
                  },
                }}
              >
                <RiRefreshLine size={20} color={naturalScale[50]} />
              </IconButton>
            </Box>
          </Box>

          <Table>
            <TableHead sx={{ bgcolor: colors.neutral.gray50 }}>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Type
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Size
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Uploaded At
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Branch
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight={600}>
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMedia.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                      }}
                    >
                      <Box>
                        <Typography variant="h5" fontWeight={600} gutterBottom>
                          Ready to Add Media?
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                          You haven't uploaded any files yet. Start adding photos, videos, or documents and keep
                          your screens fresh!
                        </Typography>
                      </Box>
                      <Box
                        component="img"
                        src={NoMediaImage}
                        alt="No media"
                        sx={{
                          width: '100%',
                          maxWidth: 200,
                          height: 'auto',
                          objectFit: 'contain',
                        }}
                      />
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<RiUploadCloudFill />}
                        onClick={() => setUploadModalOpen(true)}
                      >
                        Upload Media
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedMedia.map((media) => {
                  const fileSizeKB = (media.size / 1024).toFixed(2);
                  const dateLabel = media.uploadedAt
                    ? new Date(media.uploadedAt).toLocaleString()
                    : '-';
                  // Find the branch name from selectedBranch value
                  const currentBranch = branches.find((b) => b.value === selectedBranch);
                  const branchName = currentBranch?.name || selectedBranch || 'Unknown';

                  return (
                    <TableRow
                      key={media.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(media)}
                    >
                      {/* <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: borderRadius.xxxs,
                            overflow: 'hidden',
                            border: `1px solid ${colors.neutral.gray200}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: colors.neutral.gray50,
                            position: 'relative',
                          }}
                        >
                          {media.type === 'image' ? (
                            <>
                              <Box
                                component="img"
                                src={media.url}
                                alt={media.name}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <Box
                                sx={{
                                  display: 'none',
                                  width: '100%',
                                  height: '100%',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <RiImageCircleLine size={24} color={naturalScale[50]} />
                              </Box>
                            </>
                          ) : (
                            <>
                              <Box
                                component="video"
                                src={media.url}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                }}
                                muted
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  display: 'flex',
                                  width: '100%',
                                  height: '100%',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                                }}
                              >
                                <RiPlayLine size={24} color="white" />
                              </Box>
                              <Box
                                sx={{
                                  display: 'none',
                                  width: '100%',
                                  height: '100%',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <RiVideoLine size={24} color={naturalScale[50]} />
                              </Box>
                            </>
                          )}
                        </Box>
                      </TableCell> */}
                      <TableCell align="left" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 67,
                            height: 37,
                            // borderRadius: borderRadius.xxxs,
                            overflow: 'hidden',
                            border: `1px solid ${colors.neutral.gray200}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: colors.neutral.gray50,
                            position: 'relative',
                          }}
                        >
                          {media.type === 'image' ? (
                            <>
                              <Box
                                component="img"
                                src={media.url}
                                alt={media.name}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <Box
                                sx={{
                                  display: 'none',
                                  width: '100%',
                                  height: '100%',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <RiImageCircleLine size={24} color={naturalScale[50]} />
                              </Box>
                            </>
                          ) : (
                            <>
                              <Box
                                component="video"
                                src={media.url}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                }}
                                muted
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  display: 'flex',
                                  width: '100%',
                                  height: '100%',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                                }}
                              >
                                <RiPlayLine size={24} color="white" />
                              </Box>
                              <Box
                                sx={{
                                  display: 'none',
                                  width: '100%',
                                  height: '100%',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <RiVideoLine size={24} color={naturalScale[50]} />
                              </Box>
                            </>
                          )}
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          {media.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={media.type === 'image' ? <RiImageCircleLine size={16} color={'#010B19'} /> : <RiVideoLine size={16} color={'#010B19'} />}
                          label={media.type === 'image' ? `Image` : 'Video'}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderRadius: '5px',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {fileSizeKB} KB
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {dateLabel}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={branchName}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderRadius: '5px',
                            borderColor: colors.neutral.gray300,
                            borderWidth: 1,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleActionClick(e, media)}
                        >
                          <RiMore2Fill size={24} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {filteredMedia.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                bgcolor: naturalScale[150],
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handleChangePage}
                color="primary"
                shape="rounded"
                size="medium"
                renderItem={(item) => (
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: () => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <RiArrowLeftSLine size={16} />
                          <Typography variant="body2" sx={{ color: '#575E67' }}>
                            Previous
                          </Typography>
                        </Box>
                      ),
                      next: () => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#575E67' }}>
                            Next
                          </Typography>
                          <RiArrowRightSLine size={16} />
                        </Box>
                      ),
                    }}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: '#EFEFF0',
                        border: '1px solid #0E0E14',
                        color: '#0E0E14',
                        '&:hover': {
                          backgroundColor: '#EFEFF0',
                        },
                      },
                      '&.MuiPaginationItem-root': {
                        border: '1px solid transparent',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      },
                    }}
                  />
                )}
              />
              <Typography variant="body2" color="#575E67">
                Showing {paginatedMedia.length} of {filteredMedia.length} results
              </Typography>
            </Box>
          )}
        </TableContainer>
      </Box>


      {/* Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionClose}
      >
        <MenuItem
          onClick={() => {
            if (selectedMedia) {
              handleRowClick(selectedMedia);
            }
            handleActionClose();
          }}
        >
          <RiPlayLine size={18} style={{ marginRight: 8 }} />
          Preview
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedMedia) {
              setTvSelectionModalOpen(true);
              // Don't clear selectedMedia yet - we need it when TV is selected
              setActionMenuAnchor(null); // Just close the menu
            } else {
              handleActionClose();
            }
          }}
        >
          <RiPlayLine size={18} style={{ marginRight: 8 }} />
          Assign To TV
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedMedia) {
              handleDelete(selectedMedia.id);
            }
            handleActionClose();
          }}
          sx={{ color: colors.error.main }}
        >
          <RiDeleteBinLine size={18} style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Preview Modal */}
      <Dialog
        open={previewModalOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            bgcolor: colors.neutral.gray900,
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '70vh',
            position: 'relative',
          }}
        >
          {/* <IconButton
            onClick={handleClosePreview}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <RiDeleteBinLine size={20} />
          </IconButton> */}

          {previewMedia && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
              }}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            >
              {previewMedia.type === 'image' ? (
                <Box
                  component="img"
                  src={previewMedia.url}
                  alt={previewMedia.name}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  onSelectStart={(e) => e.preventDefault()}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    borderRadius: borderRadius.xxxs,
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    WebkitUserDrag: 'none',
                    pointerEvents: 'auto',
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <Box
                  component="video"
                  src={previewMedia.url}
                  controls
                  autoPlay
                  controlsList="nodownload"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    borderRadius: borderRadius.xxxs,
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </Box>
          )}

          {/* {previewMedia && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                p: 2,
                color: 'white',
              }}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {previewMedia.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  Type: {previewMedia.type === 'image' ? 'Image' : 'Video'}
                </Typography>
                <Typography variant="body2">
                  Size: {((previewMedia.size || 0) / 1024).toFixed(2)} KB
                </Typography>
                {previewMedia.uploadedAt && (
                  <Typography variant="body2">
                    Uploaded: {new Date(previewMedia.uploadedAt).toLocaleString()}
                  </Typography>
                )}
              </Box>
            </Box>
          )} */}
        </DialogContent>
      </Dialog>
      <Dialog
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setUploadBranch(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload files</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a branch and upload images or videos
          </Typography>

          {/* Branch Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <RiStore2Fill size={18} />
              Select Branch <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
            </Typography>
            <FormControl fullWidth required>
              <Select
                value={uploadBranch || ''}
                onChange={(e) => setUploadBranch(e.target.value)}
                displayEmpty
                required
                sx={{
                  bgcolor: colors.neutral.white,
                  borderRadius: borderRadius.xxxs,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `1px solid ${colors.neutral.gray200}`,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: `1px solid ${colors.neutral.gray300}`,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${colors.primary?.main || colors.neutral.gray900}`,
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <em>Choose a branch to upload files</em>
                </MenuItem>
                {branches.length === 0 ? (
                  <MenuItem value="" disabled>
                    No branches available
                  </MenuItem>
                ) : (
                  branches.map((branch) => (
                    <MenuItem key={branch.id || branch.value} value={branch.value}>
                      {branch.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>

          {uploadBranch ? (
            <FileUploader onUpload={handleUpload} accept="image/*,video/*" />
          ) : (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                border: `2px dashed ${colors.neutral.gray300}`,
                borderRadius: borderRadius.xxxs,
                bgcolor: colors.neutral.gray50,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Please select a branch first to upload files
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* TV Selection Dialog */}
      <Dialog
        open={tvSelectionModalOpen}
        onClose={() => {
          setTvSelectionModalOpen(false);
          setSelectedMedia(null); // Clear selected media when dialog is closed
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Media to TV</DialogTitle>
        <DialogContent>
          {!selectedBranch ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Please select a branch first to see available TVs.
            </Typography>
          ) : filteredTVs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No TVs available for the selected branch.
            </Typography>
          ) : (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a TV to assign the media:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredTVs.map((tv) => (
                  <Button
                    key={tv.id}
                    variant="outlined"
                    fullWidth
                    onClick={() => handleAssignToTV(tv)}
                    disabled={assigning}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      p: 2,
                      border: `1px solid ${colors.neutral.gray200}`,
                      '&:hover': {
                        border: `1px solid ${colors.neutral.gray300}`,
                        bgcolor: naturalScale[150],
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {tv.name || `TV-${tv.id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tv.status || 'offline'} • {tv.location || 'No location'}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MediaLibrary;

